// IMG-7: perspective correction mode — four draggable pins over the source image,
// live projected-rectangle preview, auto-rectangle / known-size targets, loupe.
// Pins live in SOURCE-pixel space (survive image moves) and persist in entity meta.
import { dist, type Vec } from "../core/geometry";
import { formatLength } from "../core/units";
import {
  autoRectSize,
  orderQuad,
  solveHomography,
  validateQuad,
  type QuadValidation
} from "../core/homography";
import type { ImageEntity } from "../doc/entities";
import { imagePxToWorld, worldToImagePx } from "../engine/imageGeom";
import { worldToScreen } from "../engine/camera";
import { drawImageEntity } from "../engine/renderImages";
import type { RenderView } from "../engine/renderEntities";
import type { Tool, ToolCtx, ToolKeyEvent, ToolPointerEvent } from "./types";

const PIN_GRAB_PX = 12;
const LOUPE_PX = 120;
const LOUPE_ZOOM = 4;

export interface PerspectivePanelState {
  active: boolean;
  mode: "auto" | "size";
  widthMm: number;
  heightMm: number;
  validation: QuadValidation;
  hasCorrection: boolean;
}

export class PerspectiveTool implements Tool {
  readonly id = "perspective" as const;
  private targetId: string | null = null;
  private pins: Vec[] = []; // source px
  private dragPin = -1;
  private cursor: Vec | null = null; // world
  private mode: "auto" | "size" = "auto";
  private widthMm = 0;
  private heightMm = 0;
  onPanelChange: (() => void) | null = null;

  constructor(private ctx: ToolCtx) {}

  /** Manager sets the target BEFORE switching to this tool. */
  setTarget(entityId: string): void {
    this.targetId = entityId;
  }

  private entity(): ImageEntity | null {
    const e = this.targetId ? this.ctx.store.doc.entities[this.targetId] : undefined;
    return e && e.type === "image" ? e : null;
  }

  /** Natural (unrectified) source pixel dims from the asset ref. */
  private srcDims(e: ImageEntity): { w: number; h: number } {
    const ref = this.ctx.store.doc.assets[e.assetHash];
    return { w: ref?.w ?? 1000, h: ref?.h ?? 1000 };
  }

  activate(): void {
    const e = this.entity();
    if (!e) {
      this.pins = []; // never render another image's pins during the dead window
      this.dragPin = -1;
      queueMicrotask(() => this.ctx.switchTool("select")); // never re-enter switchTool mid-switch
      return;
    }
    const { w, h } = this.srcDims(e);
    const saved = (e.meta?.pins as Vec[] | undefined) ?? null; // IMG-7 re-entry
    this.pins =
      saved && saved.length === 4
        ? saved.map((p) => ({ ...p }))
        : [
            { x: w * 0.1, y: h * 0.1 },
            { x: w * 0.9, y: h * 0.1 },
            { x: w * 0.9, y: h * 0.9 },
            { x: w * 0.1, y: h * 0.9 }
          ];
    if (this.mode === "auto" || this.widthMm <= 0) {
      this.widthMm = e.wMm;
      this.heightMm = e.hMm;
    }
    this.ctx.selection.set([e.id]);
    this.ctx.setMeasure("none", "");
    this.syncHint();
    this.ctx.requestRender();
    this.onPanelChange?.();
  }

  deactivate(): void {
    this.dragPin = -1;
    this.onPanelChange?.();
  }

  /** TL-1: discard in-progress state only — switching tools is the caller's job. */
  cancel(): void {
    this.dragPin = -1;
    this.ctx.gestureActive(false);
  }

  /** Esc / panel Cancel: leave perspective mode. */
  exit(): void {
    this.cancel();
    this.ctx.switchTool("select");
  }

  inProgress(): boolean {
    return true; // suppress context menu while adjusting (TL-2)
  }

  private syncHint(): void {
    const v = this.panelState().validation;
    this.ctx.setHint(
      v.ok
        ? "Perspective: drag the four pins to the corners of something rectangular · Apply in the panel · Esc = cancel"
        : `Perspective: ${v.reason} — keep dragging the pins`
    );
  }

  panelState(): PerspectivePanelState {
    const e = this.entity();
    const dims = e ? this.srcDims(e) : { w: 1, h: 1 };
    return {
      active: !!e,
      mode: this.mode,
      widthMm: this.widthMm,
      heightMm: this.heightMm,
      validation:
        this.pins.length === 4
          ? validateQuad(orderQuad(this.pins), 0.005 * dims.w * dims.h)
          : { ok: false, reason: "Four corners required" },
      hasCorrection: !!e?.homography
    };
  }

  setMode(mode: "auto" | "size"): void {
    this.mode = mode;
    this.onPanelChange?.();
  }

  setSize(widthMm: number, heightMm: number): void {
    if (widthMm > 0) this.widthMm = widthMm;
    if (heightMm > 0) this.heightMm = heightMm;
    this.onPanelChange?.();
  }

  // ---- pointer -------------------------------------------------------------

  private pinAtWorld(world: Vec): number {
    const e = this.entity();
    if (!e) return -1;
    const { w, h } = this.srcDims(e);
    const tol = PIN_GRAB_PX / this.ctx.scale();
    let best = -1;
    let bestD = Infinity;
    this.pins.forEach((pin, i) => {
      const pw = imagePxToWorld(this.displayEntity(e), pin, w, h);
      const d = dist(pw, world);
      if (d <= tol && d < bestD) {
        best = i;
        bestD = d;
      }
    });
    return best;
  }

  /** Pins are placed over the ORIGINAL (unrectified) image; preview shows it raw. */
  private displayEntity(e: ImageEntity): ImageEntity {
    if (!e.homography) return e;
    return { ...e, homography: undefined, rectifiedW: undefined, rectifiedH: undefined };
  }

  pointerDown(e: ToolPointerEvent): void {
    if (e.button !== 0) return;
    this.dragPin = this.pinAtWorld(e.world);
    this.ctx.gestureActive(this.dragPin >= 0);
  }

  pointerMove(e: ToolPointerEvent): void {
    this.cursor = e.world;
    if (this.dragPin >= 0) {
      const entity = this.entity();
      if (entity) {
        const { w, h } = this.srcDims(entity);
        const px = worldToImagePx(this.displayEntity(entity), e.world, w, h);
        this.pins[this.dragPin] = {
          x: Math.max(0, Math.min(w, px.x)),
          y: Math.max(0, Math.min(h, px.y))
        };
        this.syncHint();
        this.onPanelChange?.();
      }
    }
    this.ctx.requestRender();
  }

  pointerUp(): void {
    this.dragPin = -1;
    this.ctx.gestureActive(false);
  }

  doubleClick(): void {}

  key(e: ToolKeyEvent): boolean {
    if (e.key === "Escape") {
      this.exit();
      return true;
    }
    if (e.key === "Enter") {
      this.apply();
      return true;
    }
    return false;
  }

  measurement(): boolean {
    return false;
  }

  // ---- apply / remove ---------------------------------------------------------

  /** IMG-7 Apply: rectify (+calibrate in size mode) as ONE command; pins persist in meta. */
  apply(): void {
    const e = this.entity();
    if (!e) return;
    const state = this.panelState();
    if (!state.validation.ok) return;
    const ordered = orderQuad(this.pins);
    const auto = autoRectSize(ordered); // rectified size in source px
    const rw = Math.max(16, Math.round(auto.w));
    const rh =
      this.mode === "size" && this.widthMm > 0 && this.heightMm > 0
        ? Math.max(16, Math.round((rw * this.heightMm) / this.widthMm))
        : Math.max(16, Math.round(auto.h));
    const h = solveHomography(ordered, [
      { x: 0, y: 0 },
      { x: rw, y: 0 },
      { x: rw, y: rh },
      { x: 0, y: rh }
    ]);
    if (!h) {
      this.ctx.setHint("Perspective: corners are degenerate — adjust the pins");
      return;
    }
    const newWmm = this.mode === "size" ? this.widthMm : e.wMm;
    const newHmm = this.mode === "size" ? this.heightMm : (e.wMm * rh) / rw;
    this.ctx.store.dispatch({
      kind: "updateEntities",
      patches: [
        {
          id: e.id,
          before: {
            homography: e.homography,
            rectifiedW: e.rectifiedW,
            rectifiedH: e.rectifiedH,
            wMm: e.wMm,
            hMm: e.hMm,
            crop: e.crop,
            meta: e.meta
          },
          after: {
            homography: h,
            rectifiedW: rw,
            rectifiedH: rh,
            wMm: newWmm,
            hMm: newHmm,
            crop: undefined,
            meta: { ...e.meta, pins: this.pins.map((p) => ({ ...p })) }
          }
        }
      ]
    });
    this.ctx.assets.dropRectified(e.assetHash);
    this.ctx.switchTool("select");
  }

  removeCorrection(): void {
    const e = this.entity();
    if (!e || !e.homography) return;
    this.ctx.store.dispatch({
      kind: "updateEntities",
      patches: [
        {
          id: e.id,
          before: { homography: e.homography, rectifiedW: e.rectifiedW, rectifiedH: e.rectifiedH },
          after: { homography: undefined, rectifiedW: undefined, rectifiedH: undefined }
        }
      ]
    });
    this.ctx.switchTool("select");
  }

  // ---- rendering ----------------------------------------------------------------

  renderDynamic(v: RenderView): void {
    const e = this.entity();
    if (!e) return;
    const { ctx } = v;
    const { w, h } = this.srcDims(e);
    const display = this.displayEntity(e);
    const px = (n: number): number => n / v.cam.scale;

    // IMG-7: dim everything, then show the raw image full-strength
    ctx.save();
    ctx.setTransform(v.dpr, 0, 0, v.dpr, 0, 0);
    ctx.fillStyle = "rgba(10, 11, 15, 0.8)";
    ctx.fillRect(0, 0, v.cssW, v.cssH);
    ctx.restore();
    drawImageEntity(v, { ...display, opacity: 1 }, undefined, this.ctx.assets);

    const worldPins = this.pins.map((p) => imagePxToWorld(display, p, w, h));

    // projected rectangle + 3×3 interior grid preview
    const valid = this.panelState().validation.ok;
    ctx.strokeStyle = valid ? "#4dd0e1" : "#e05c5c";
    ctx.lineWidth = px(1.5);
    ctx.setLineDash([px(6), px(4)]);
    ctx.beginPath();
    worldPins.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);
    if (valid) {
      ctx.lineWidth = px(0.75);
      ctx.globalAlpha = 0.6;
      const ordered = orderQuad(worldPins);
      for (let i = 1; i < 3; i++) {
        const t = i / 3;
        const top = lerpV(ordered[0]!, ordered[1]!, t);
        const bottom = lerpV(ordered[3]!, ordered[2]!, t);
        const left = lerpV(ordered[0]!, ordered[3]!, t);
        const right = lerpV(ordered[1]!, ordered[2]!, t);
        ctx.beginPath();
        ctx.moveTo(top.x, top.y);
        ctx.lineTo(bottom.x, bottom.y);
        ctx.moveTo(left.x, left.y);
        ctx.lineTo(right.x, right.y);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    // pins
    worldPins.forEach((p, i) => {
      ctx.fillStyle = i === this.dragPin ? "#e0b34d" : "#4dd0e1";
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = px(1.5);
      ctx.beginPath();
      ctx.arc(p.x, p.y, px(7), 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    });

    // loupe near the dragged pin (IMG-7)
    if (this.dragPin >= 0 && this.cursor) {
      this.drawLoupe(v, display, w, h);
    }

    if (this.mode === "size") {
      const { units, precision } = this.ctx.docUnits();
      this.ctx.setMeasure(
        "none",
        `${formatLength(this.widthMm, units, precision)} × ${formatLength(this.heightMm, units, precision)}`
      );
    }
  }

  private drawLoupe(v: RenderView, display: ImageEntity, srcW: number, srcH: number): void {
    const cached = this.ctx.assets.get(display.assetHash);
    if (!cached || !this.cursor) return;
    const { ctx } = v;
    const screen = worldToScreen(v.cam, v.cssW, v.cssH, this.cursor);
    const cx = screen.x + LOUPE_PX * 0.8;
    const cy = screen.y - LOUPE_PX * 0.8;
    const srcPt = worldToImagePx(display, this.cursor, srcW, srcH);
    const half = LOUPE_PX / 2 / LOUPE_ZOOM / (v.cam.scale * (display.wMm / srcW)); // src px per loupe half — simplify: fixed src window
    const win = Math.max(8, LOUPE_PX / 2 / LOUPE_ZOOM) * (srcW / (display.wMm * v.cam.scale));
    void half;
    ctx.save();
    ctx.setTransform(v.dpr, 0, 0, v.dpr, 0, 0);
    ctx.beginPath();
    ctx.arc(cx, cy, LOUPE_PX / 2, 0, Math.PI * 2);
    ctx.fillStyle = "#14161c";
    ctx.fill();
    ctx.clip();
    ctx.drawImage(
      cached.levels[0]!,
      srcPt.x - win,
      srcPt.y - win,
      win * 2,
      win * 2,
      cx - LOUPE_PX / 2,
      cy - LOUPE_PX / 2,
      LOUPE_PX,
      LOUPE_PX
    );
    // crosshair
    ctx.strokeStyle = "#e0b34d";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx - 10, cy);
    ctx.lineTo(cx + 10, cy);
    ctx.moveTo(cx, cy - 10);
    ctx.lineTo(cx, cy + 10);
    ctx.stroke();
    ctx.restore();
    ctx.strokeStyle = "#4dd0e1";
    ctx.lineWidth = 1.5 / v.cam.scale;
  }
}

function lerpV(a: Vec, b: Vec, t: number): Vec {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}
